import React, { useState, useEffect, useCallback } from 'react';
import Summary from './components/Summary';
import ClickHeatmap from './components/ClickHeatmap';
import ScrollDepth from './components/ScrollDepth';
import MouseMovement from './components/MouseMovement';
import SessionReplay from './components/SessionReplay';
import AIAnalysis from './components/AIAnalysis';
import LPManager from './components/LPManager';
import './App.css';

const API = process.env.REACT_APP_API_URL || '/api';

function App() {
  const [tab, setTab] = useState('click');
  const [summary, setSummary] = useState(null);
  const [clicks, setClicks] = useState([]);
  const [scrollData, setScrollData] = useState([]);
  const [moves, setMoves] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lps, setLps] = useState([]);
  const [selectedLp, setSelectedLp] = useState(null);

  const lpQuery = selectedLp ? `?lpId=${selectedLp}` : '';

  const fetchLps = useCallback(async () => {
    try {
      const res = await fetch(`${API}/lps`);
      setLps(await res.json());
    } catch (e) {
      console.error('Failed to fetch LPs:', e);
    }
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [sumRes, clickRes, scrollRes, moveRes, sessRes] = await Promise.all([
        fetch(`${API}/data/summary${lpQuery}`),
        fetch(`${API}/data/clicks${lpQuery}`),
        fetch(`${API}/data/scroll${lpQuery}`),
        fetch(`${API}/data/moves${lpQuery}`),
        fetch(`${API}/data/sessions${lpQuery}`),
      ]);
      setSummary(await sumRes.json());
      setClicks(await clickRes.json());
      setScrollData(await scrollRes.json());
      setMoves(await moveRes.json());
      setSessions(await sessRes.json());
    } catch (e) {
      console.error('Failed to fetch data:', e);
    }
    setLoading(false);
  }, [lpQuery]);

  useEffect(() => {
    fetchLps();
  }, [fetchLps]);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 10000);
    return () => clearInterval(interval);
  }, [fetchData]);

  const handleClear = async () => {
    if (!window.confirm('すべてのデータを削除しますか？')) return;
    await fetch(`${API}/data/clear${lpQuery}`, { method: 'DELETE' });
    fetchData();
  };

  // Determine LP preview URL
  const baseUrl = process.env.REACT_APP_API_URL
    ? process.env.REACT_APP_API_URL.replace('/api', '')
    : '';
  const lpUrl = selectedLp
    ? `${baseUrl}/api/lp/${selectedLp}`
    : `${baseUrl}/api/lp`;

  const tabs = [
    { id: 'click', label: 'クリックヒートマップ', icon: '🖱️' },
    { id: 'scroll', label: 'スクロール到達率', icon: '📜' },
    { id: 'mouse', label: 'マウス移動', icon: '🔥' },
    { id: 'session', label: 'セッション再生', icon: '▶️' },
    { id: 'ai', label: 'AI分析', icon: '🤖' },
  ];

  return (
    <div className="app">
      <header className="header">
        <h1>LP ヒートマップ分析</h1>
        <div className="header-actions">
          <button className="btn-refresh" onClick={fetchData} disabled={loading}>
            {loading ? '読込中...' : '更新'}
          </button>
          <button className="btn-clear" onClick={handleClear}>データクリア</button>
        </div>
      </header>

      <LPManager
        lps={lps}
        selectedLp={selectedLp}
        onSelect={(id) => setSelectedLp(id)}
        onRefresh={fetchLps}
      />

      {summary && <Summary data={summary} />}

      <nav className="tabs">
        {tabs.map(t => (
          <button
            key={t.id}
            className={`tab ${tab === t.id ? 'active' : ''}`}
            onClick={() => setTab(t.id)}
          >
            <span className="tab-icon">{t.icon}</span>
            {t.label}
          </button>
        ))}
      </nav>

      <main className="main">
        {tab === 'click' && <ClickHeatmap data={clicks} lpUrl={lpUrl} />}
        {tab === 'scroll' && <ScrollDepth data={scrollData} />}
        {tab === 'mouse' && <MouseMovement data={moves} />}
        {tab === 'session' && <SessionReplay sessions={sessions} lpUrl={lpUrl} lpQuery={lpQuery} />}
        {tab === 'ai' && <AIAnalysis lpId={selectedLp} />}
      </main>
    </div>
  );
}

export default App;
